/**
 * @file Helpers for managing cookies.
 */

/**
 * Options for setting a cookie.
 */
export type CookieOptionsType = {
  expires?: string | Date;
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: "Strict" | "Lax" | "None";
  httpOnly?: boolean;
};

/**
 * Retrieves the value of a specified cookie.
 *
 * @param name - The name of the cookie to retrieve.
 * @param decodeVal - A boolean indicating whether to decode the cookie value. Defaults to `true`.
 * @param defaultVal - The default value to return if the cookie is not found. Defaults to an empty string.
 * @returns The value of the cookie, decoded if `decodeVal` is `true`, or the default value if the cookie is not found.
 */
export function getCookieVal(
  name: string,
  decodeVal: boolean = true,
  defaultVal: string = ""
): string {
  if (!window.document || !window.document.cookie) {
    return "";
  }
  const reCookieName = window.document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  const cookieVal = reCookieName ? reCookieName[2] : defaultVal;

  if (decodeVal === true) {
    return decodeURIComponent(cookieVal);
  }

  return cookieVal;
}

/**
 * Sets a cookie with the specified name, value, and options.
 *
 * @param name - The name of the cookie to set.
 * @param value - The value to store in the cookie.
 * @param options - Options for setting the cookie.
 */
export function setCookie(
  name: string,
  value: string | object,
  options: CookieOptionsType = {}
): void {
  if (!window.document) {
    return;
  }

  const { expires, domain, path = "/", secure = false, sameSite, httpOnly = false } = options;

  let cookieValue = value;

  // Check if the value is an object, stringify and URI encode it
  if (typeof value === "object" && value !== null) {
    try {
      cookieValue = encodeURIComponent(JSON.stringify(value));
    } catch (error) {
      console.error("Error while parsing JSON data:", error);
    }
  }

  let cookieString = `${name}=${cookieValue}`;

  if (expires instanceof Date) {
    cookieString += `; expires=${expires.toUTCString()}`;
  } else if (typeof expires === "string") {
    const expiresDate = new Date(expires);
    cookieString += `; expires=${expiresDate.toUTCString()}`;
  }

  if (domain) {
    cookieString += `; domain=${domain}`;
  }
  if (path) {
    cookieString += `; path=${path}`;
  }
  if (secure) {
    cookieString += `; secure`;
  }
  if (sameSite) {
    cookieString += `; samesite=${sameSite}`;
  }
  if (httpOnly) {
    cookieString += `; HttpOnly`;
  }

  document.cookie = cookieString;
}

/**
 * Generates a SHA-256 hash of the provided message.
 *
 * @param message - The string to hash.
 * @returns A promise that resolves to the hex string representation of the hash.
 */
export async function sha256(message: string): Promise<string> {
  // encode as UTF-8
  const msgBuffer = new TextEncoder().encode(message);

  // hash the message
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);

  // convert ArrayBuffer to Array
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  // convert bytes to hex string
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  return hashHex;
}

/**
 * Converts an email address to its hashed value.
 *
 * @param email - The email address to hash.
 * @param backupVal - The value to return in case of error or invalid input. Defaults to empty string.
 * @returns A promise that resolves to the hashed email or the backup value.
 */
export async function toEhashAsync(email: string, backupVal = ""): Promise<string> {
  if (!email || typeof email !== "string") {
    return Promise.resolve(backupVal);
  }

  try {
    return await sha256(email.toLowerCase());
  } catch (error) {
    return backupVal;
  }
}

/**
 * Sets a cookie with a hashed version of the provided email.
 * The cookie will be set with name "SESSehash", expires 13 months from now,
 * and domain ".worldvision.org".
 *
 * @param email - The email address to hash and store.
 * @returns A promise that resolves when the cookie is set.
 */
export async function setEhashCookieAsync(email: string): Promise<void> {
  const dateExpires = new Date();
  dateExpires.setMonth(dateExpires.getMonth() + 13);

  const ehash = await toEhashAsync(email).catch((err) => {
    console.log(err);
    return "";
  });

  if (!ehash) {
    return;
  }

  setCookie("SESSehash", ehash, {
    expires: dateExpires,
    domain: ".worldvision.org"
  });
}
